import { PlanBoardAPI } from 'api/plan-board';
import { getProjectFromCollection, projectCollection } from 'collections/project';
import { taskCollection } from 'collections/tasks';
import { extractParts, PhaseEntityType } from 'data/projects/phase-entity';
import { logger } from 'services';
import { refreshPlanBoard, refreshPlanLanesForPhase } from './fetchers';
import { planFunnel } from './telemetry';
import { getPlanFiltersHash, planBoardPhaseCollection, planLaneCollection } from './plan-lane-collection';
/**
 * WRITE layer for the paginated plan board.
 *
 * Verify-then-skip refetch policy: optimistic patches (laneBus view order +
 * the collection patches below) already put the client in the state the
 * server is expected to confirm. On settle we VERIFY the server's
 * `EntityReorderResult` against the optimistic state and only refetch —
 * fire-and-forget, never blocking the caller — when they disagree (stale
 * anchor degraded server-side, a concurrent edit, a filtered/partial window
 * we can't verify). On failure the same background refetch is the rollback.
 * Errors are logged with full context and rethrown so the calling hook can
 * surface a user-facing toast (never swallowed).
 */ const DEFAULT_FILTERS_HASH = getPlanFiltersHash(undefined);
/**
 * Does the optimistic lane state agree with the server's resolved position?
 * Only unfiltered lanes are position-verifiable (their token window is a
 * prefix of the canonical order, so window index == canonical index);
 * filtered lanes verify membership only. A token beyond the loaded window
 * (deep-lane BOTTOM move) fails the index check → background refetch.
 */ const lanesMatchResolvedPosition = (projectId, phaseId, entityId, resolvedPosition)=>{
    const lanes = planLaneCollection.getByIndex('byPhase', phaseId).filter((lane)=>lane.projectId === projectId);
    return lanes.every((lane)=>{
        const index = lane.pageTokens.indexOf(entityId);
        if (lane.filtersHash !== DEFAULT_FILTERS_HASH) {
            return index !== -1;
        }
        return resolvedPosition != null && index === resolvedPosition;
    });
};
/** Anchor-based in-lane reorder. */ export const reorderLaneEntity = async (projectId, phaseId, payload)=>{
    planFunnel.reorderStart({
        phaseId,
        entityId: payload.entityId
    });
    try {
        const result = await PlanBoardAPI.reorderEntity(phaseId, payload);
        if (!lanesMatchResolvedPosition(projectId, phaseId, payload.entityId, result.resolvedPosition)) {
            // Server resolved a different order than the optimistic one —
            // reconcile in the background (the refresh helper never rejects).
            planFunnel.reorderReconcile({
                phaseId,
                entityId: payload.entityId
            });
            void refreshPlanLanesForPhase(projectId, phaseId);
        } else {
            planFunnel.reorderVerified({
                phaseId,
                entityId: payload.entityId
            });
        }
        return result;
    } catch (error) {
        logger.error('plan-board reorderLaneEntity failed', {
            error,
            projectId,
            phaseId,
            payload
        });
        planFunnel.reorderError({
            phaseId,
            entityId: payload.entityId
        });
        // Roll the optimistic laneBus order back to server truth — in the
        // background; the caller only needs the rejection for its toast.
        void refreshPlanLanesForPhase(projectId, phaseId);
        throw error;
    }
};
/**
 * Optimistically rewrite each loaded lane's pageTokens to the dropped in-lane
 * order so reorderLaneEntity's resolve-position check agrees with the view.
 * Without this, an agreed reorder still triggers a background reconcile
 * because pageTokens stayed at the pre-drag order (SOURCE BUG #134).
 */ export const reorderPlanEntityTokensOptimistic = (projectId, phaseId, newOrder)=>{
    const lanes = planLaneCollection.getByIndex('byPhase', phaseId).filter((lane)=>lane.projectId === projectId);
    if (lanes.length === 0) {
        return;
    }
    planLaneCollection.push(lanes.map((lane)=>{
        const orderIndex = new Map(newOrder.map((token, index)=>[
                token,
                index
            ]));
        const inWindow = lane.pageTokens.filter((token)=>orderIndex.has(token));
        const outsideWindow = lane.pageTokens.filter((token)=>!orderIndex.has(token));
        inWindow.sort((left, right)=>{
            var _orderIndex_get, _orderIndex_get1;
            return ((_orderIndex_get = orderIndex.get(left)) !== null && _orderIndex_get !== void 0 ? _orderIndex_get : 0) - ((_orderIndex_get1 = orderIndex.get(right)) !== null && _orderIndex_get1 !== void 0 ? _orderIndex_get1 : 0);
        });
        return {
            ...lane,
            pageTokens: [
                ...inWindow,
                ...outsideWindow
            ]
        };
    }));
};
/**
 * Optimistically move `entityId`'s token between the two phases' lane
 * records. Lanes render by joining `pageTokens` live against
 * `taskCollection`, so the laneBus (view-state) order alone can't make the
 * card appear in the destination lane — the moved token isn't in that lane's
 * record, so `useLaneEntities` never hands the destination its task. Patch
 * the records themselves: remove from every source-phase lane, insert into
 * every destination-phase lane at the anchor-resolved slot, and shift the
 * lane + board counts. Verification on settle (or the failure rollback)
 * triggers a background refetch when the server disagrees, so a mid-window
 * BOTTOM approximation or a rejected move self-corrects.
 */ export const movePlanEntityTokensOptimistic = (projectId, destPhaseId, payload)=>{
    const { entityId, fromPhaseId } = payload;
    // Patch the task's own phase in taskCollection (push-only, no API — the
    // move endpoint persists it). Cards, filters and the task drawer read
    // `projectPhase` off the collection task; without this the moved card
    // renders in the destination lane while its data still claims the source
    // phase until the post-move refresh.
    const parts = extractParts(entityId);
    if (parts.entityType === PhaseEntityType.Task) {
        var _planBoardPhaseCollection_get;
        const task = taskCollection.get(parts.id);
        const destPhase = (_planBoardPhaseCollection_get = planBoardPhaseCollection.get(destPhaseId)) === null || _planBoardPhaseCollection_get === void 0 ? void 0 : _planBoardPhaseCollection_get.phase;
        if (task) {
            var _task_projectPhase;
            taskCollection.push([
                {
                    ...task,
                    projectPhase: {
                        ...(_task_projectPhase = task.projectPhase) !== null && _task_projectPhase !== void 0 ? _task_projectPhase : {},
                        projectPhaseId: destPhaseId,
                        ...(destPhase === null || destPhase === void 0 ? void 0 : destPhase.projectPhaseName) != null ? {
                            projectPhaseName: destPhase.projectPhaseName
                        } : {}
                    }
                }
            ]);
        }
    }
    const sourceLanes = planLaneCollection.getByIndex('byPhase', fromPhaseId).filter((lane)=>lane.projectId === projectId && lane.pageTokens.includes(entityId));
    const destLanes = planLaneCollection.getByIndex('byPhase', destPhaseId).filter((lane)=>lane.projectId === projectId);
    if (sourceLanes.length > 0) {
        planLaneCollection.push(sourceLanes.map((lane)=>({
                ...lane,
                pageTokens: lane.pageTokens.filter((token)=>token !== entityId),
                totalCount: Math.max(0, lane.totalCount - 1),
                filteredCount: Math.max(0, lane.filteredCount - 1)
            })));
    }
    if (destLanes.length > 0) {
        planLaneCollection.push(destLanes.map((lane)=>{
            const tokens = lane.pageTokens.filter((token)=>token !== entityId);
            const afterIndex = payload.afterEntityId ? tokens.indexOf(payload.afterEntityId) : -1;
            const beforeIndex = payload.beforeEntityId ? tokens.indexOf(payload.beforeEntityId) : -1;
            const insertAt = afterIndex !== -1 ? afterIndex + 1 : beforeIndex !== -1 ? beforeIndex : payload.anchor === 'TOP' ? 0 : tokens.length;
            tokens.splice(insertAt, 0, entityId);
            return {
                ...lane,
                pageTokens: tokens,
                totalCount: lane.totalCount + 1,
                filteredCount: lane.filteredCount + 1
            };
        }));
    }
    // Keep the project detail's phase entityOrder coherent too — paginated
    // lanes don't render from it, but the gantt and the date-shift math still
    // read it. Dedup-safe: strip the token from BOTH phases' orders first, then
    // insert at the anchor-resolved slot in the destination (re-applying an
    // order a caller already patched resolves to the same final state).
    const project = getProjectFromCollection(projectId);
    if (project === null || project === void 0 ? void 0 : project.projectPhases) {
        const projectPhases = project.projectPhases.map((phase)=>{
            var _phase_entityOrder;
            if (phase.projectPhaseId !== fromPhaseId && phase.projectPhaseId !== destPhaseId) {
                return phase;
            }
            const order = ((_phase_entityOrder = phase.entityOrder) !== null && _phase_entityOrder !== void 0 ? _phase_entityOrder : []).filter((token)=>token !== entityId);
            if (phase.projectPhaseId === destPhaseId) {
                const afterIndex = payload.afterEntityId ? order.indexOf(payload.afterEntityId) : -1;
                const beforeIndex = payload.beforeEntityId ? order.indexOf(payload.beforeEntityId) : -1;
                const insertAt = afterIndex !== -1 ? afterIndex + 1 : beforeIndex !== -1 ? beforeIndex : payload.anchor === 'TOP' ? 0 : order.length;
                order.splice(insertAt, 0, entityId);
            }
            return {
                ...phase,
                entityOrder: order
            };
        });
        projectCollection.push([
            {
                ...project,
                projectPhases
            }
        ]);
    }
    // Board header counts (ghost sizing) — best-effort ±1; a verification
    // mismatch on settle refetches the board as the truth.
    const boardPatches = [
        fromPhaseId,
        destPhaseId
    ].map((phaseId, i)=>{
        const entry = planBoardPhaseCollection.get(phaseId);
        if (!entry || entry.projectId !== projectId) {
            return undefined;
        }
        const delta = i === 0 ? -1 : 1;
        return {
            ...entry,
            entityCount: Math.max(0, entry.entityCount + delta)
        };
    }).filter((entry)=>entry != null);
    if (boardPatches.length > 0) {
        planBoardPhaseCollection.push(boardPatches);
    }
};
/**
 * Fields of a phase edit that are safe to patch onto the cached phase beans.
 * Whitelisted (instead of spreading the whole payload/response) so a partial
 * payload can never clobber server-owned aggregates living on the same bean
 * (progressStatus, totalTaskCount, entity counts).
 */ const PHASE_PATCH_FIELDS = [
    'projectPhaseName',
    'startDate',
    'dueDate',
    'phaseDescription',
    'private'
];
/**
 * Apply a phase-edit payload onto a cached phase bean: whitelisted scalars
 * replace, and payload `fields` (e.g. the header status pill's single Status
 * field) MERGE by fieldId — a partial fields payload must never drop the
 * bean's other fields.
 */ const applyPhasePatch = (phase, phasePatch)=>{
    var _phasePatch_fields;
    const next = {
        ...phase
    };
    PHASE_PATCH_FIELDS.forEach((field)=>{
        if (phasePatch[field] !== undefined) {
            // Object.assign keeps the write typed (field is keyof ProjectPhase) —
            // avoids a Record<string, unknown> cast that tsc rejects as a
            // non-overlapping conversion.
            Object.assign(next, {
                [field]: phasePatch[field]
            });
        }
    });
    if ((_phasePatch_fields = phasePatch.fields) === null || _phasePatch_fields === void 0 ? void 0 : _phasePatch_fields.length) {
        var _phase_fields;
        const patchById = new Map(phasePatch.fields.map((field)=>[
                field.fieldId,
                field
            ]));
        const existing = (_phase_fields = phase.fields) !== null && _phase_fields !== void 0 ? _phase_fields : [];
        const merged = existing.map((field)=>patchById.has(field.fieldId) ? {
                ...field,
                ...patchById.get(field.fieldId)
            } : field);
        phasePatch.fields.forEach((field)=>{
            if (!existing.some((e)=>e.fieldId === field.fieldId)) {
                merged.push(field);
            }
        });
        next.fields = merged;
    }
    return next;
};
const hasPhasePatchContent = (phasePatch)=>{
    var _phasePatch_fields;
    return Boolean((_phasePatch_fields = phasePatch.fields) === null || _phasePatch_fields === void 0 ? void 0 : _phasePatch_fields.length) || PHASE_PATCH_FIELDS.some((field)=>phasePatch[field] !== undefined);
};
/**
 * Would applying this patch change anything the bean doesn't already hold?
 * Used to SKIP the settle-time server-truth push when it matches the
 * optimistic patch — re-pushing identical values costs a full lane commit
 * (~1s at 20x CPU) for zero visual change.
 */ const phasePatchIsNoop = (phase, phasePatch)=>{
    var _phasePatch_fields;
    const scalarsEqual = PHASE_PATCH_FIELDS.every((field)=>phasePatch[field] === undefined || phasePatch[field] === phase[field]);
    if (!scalarsEqual) {
        return false;
    }
    return ((_phasePatch_fields = phasePatch.fields) !== null && _phasePatch_fields !== void 0 ? _phasePatch_fields : []).every((patchField)=>{
        var _phase_fields;
        const existing = ((_phase_fields = phase.fields) !== null && _phase_fields !== void 0 ? _phase_fields : []).find((field)=>field.fieldId === patchField.fieldId);
        return existing != null && existing.fieldValue === patchField.fieldValue;
    });
};
/**
 * Patch a phase's editable fields in BOTH cached homes — the phase inside the
 * project detail and the plan-board phase record — so a phase edit (modal
 * save, header status pill) paints immediately instead of waiting for the
 * API + detail refetch. Also used with the server response on settle, making
 * the eventual state authoritative. Undefined payload fields are skipped.
 */ export const patchProjectPhaseInStores = (projectId, phasePatch, options = {})=>{
    var _project_projectPhases;
    const phaseId = phasePatch.projectPhaseId;
    if (phaseId == null || !hasPhasePatchContent(phasePatch)) {
        return;
    }
    const boardEntry = planBoardPhaseCollection.get(phaseId);
    if (boardEntry && boardEntry.projectId === projectId && !phasePatchIsNoop(boardEntry.phase, phasePatch)) {
        planBoardPhaseCollection.push([
            {
                ...boardEntry,
                phase: applyPhasePatch(boardEntry.phase, phasePatch)
            }
        ]);
    }
    if (options.targets === 'board-only') {
        return;
    }
    const project = getProjectFromCollection(projectId);
    const detailPhase = project === null || project === void 0 ? void 0 : (_project_projectPhases = project.projectPhases) === null || _project_projectPhases === void 0 ? void 0 : _project_projectPhases.find((p)=>p.projectPhaseId === phaseId);
    if (project && detailPhase && !phasePatchIsNoop(detailPhase, phasePatch)) {
        projectCollection.push([
            {
                ...project,
                projectPhases: project.projectPhases.map((p)=>p.projectPhaseId === phaseId ? applyPhasePatch(p, phasePatch) : p)
            }
        ]);
    }
};
/**
 * Optimistically reorder the project's phases to `phaseOrder`. Plan lanes
 * render phase order straight off `project.projectPhases` array order
 * (project-plan.tsx maps it to `projectPhaseIds`), so reordering the array
 * repaints the lanes in the new order in the same tick — no API wait. Phases
 * not named in `phaseOrder` keep their existing relative order at the tail
 * (defensive; the modal always passes every phase). Returns a rollback thunk
 * that restores the prior phases onto the CURRENT project object, so
 * concurrent changes to other (non-phase) project fields survive the revert.
 */ export const reorderProjectPhasesOptimistic = (projectId, phaseOrder)=>{
    var _project_projectPhases;
    const project = getProjectFromCollection(projectId);
    if (!(project === null || project === void 0 ? void 0 : (_project_projectPhases = project.projectPhases) === null || _project_projectPhases === void 0 ? void 0 : _project_projectPhases.length)) {
        return ()=>undefined;
    }
    const previousPhases = project.projectPhases;
    const byId = new Map(previousPhases.map((phase)=>[
            phase.projectPhaseId,
            phase
        ]));
    const orderSet = new Set(phaseOrder);
    const reordered = [
        ...phaseOrder.map((id)=>byId.get(id)).filter((phase)=>phase != null),
        ...previousPhases.filter((phase)=>!orderSet.has(phase.projectPhaseId))
    ];
    projectCollection.push([
        {
            ...project,
            projectPhases: reordered
        }
    ]);
    return ()=>{
        const current = getProjectFromCollection(projectId);
        if (current) {
            projectCollection.push([
                {
                    ...current,
                    projectPhases: previousPhases
                }
            ]);
        }
    };
};
/**
 * Marking a phase private marks every task in it private (server cascade in
 * ProjectPhaseService.updatePhasePrivacy). Mirror that on the LOADED tasks so
 * cards flip their privacy immediately with the optimistic phase patch —
 * unloaded window pages get server truth when fetched. Shared (un-private)
 * has NO server cascade, so only the private direction is mirrored; callers
 * refresh the phase's lanes on settle for the authoritative task state.
 * Returns the number of loaded tasks flipped (for funnel telemetry).
 */ export const cascadePhasePrivacyToLoadedTasks = (phaseId)=>{
    const tasksToPatch = taskCollection.getAll().filter((task)=>{
        var _task_projectPhase;
        return ((_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId) === phaseId && task.private !== true;
    });
    if (tasksToPatch.length > 0) {
        taskCollection.push(tasksToPatch.map((task)=>({
                ...task,
                private: true
            })));
    }
    return tasksToPatch.length;
};
/** Cross-phase move — verify-then-skip refetch, never blocking the caller. */ export const movePlanEntity = async (projectId, destPhaseId, payload)=>{
    const refreshBothInBackground = ()=>{
        void Promise.all([
            refreshPlanLanesForPhase(projectId, payload.fromPhaseId),
            refreshPlanLanesForPhase(projectId, destPhaseId),
            refreshPlanBoard(projectId)
        ]).catch(()=>undefined);
    };
    planFunnel.moveStart({
        entityId: payload.entityId,
        fromPhaseId: payload.fromPhaseId,
        toPhaseId: destPhaseId
    });
    // Token-level optimistic move so the card renders in the destination lane
    // immediately (see movePlanEntityTokensOptimistic).
    movePlanEntityTokensOptimistic(projectId, destPhaseId, payload);
    planFunnel.moveOptimistic({
        entityId: payload.entityId,
        toPhaseId: destPhaseId
    });
    try {
        const result = await PlanBoardAPI.moveEntity(projectId, destPhaseId, payload);
        const sourceClean = planLaneCollection.getByIndex('byPhase', payload.fromPhaseId).filter((lane)=>lane.projectId === projectId).every((lane)=>!lane.pageTokens.includes(payload.entityId));
        const verified = result.projectPhaseId === destPhaseId && sourceClean && lanesMatchResolvedPosition(projectId, destPhaseId, payload.entityId, result.resolvedPosition);
        if (!verified) {
            planFunnel.moveReconcile({
                entityId: payload.entityId,
                toPhaseId: destPhaseId,
                reason: result.projectPhaseId !== destPhaseId ? 'phase_mismatch' : !sourceClean ? 'source_dirty' : 'position_mismatch'
            });
            refreshBothInBackground();
        } else {
            planFunnel.moveVerified({
                entityId: payload.entityId,
                toPhaseId: destPhaseId
            });
        }
        return result;
    } catch (error) {
        logger.error('plan-board movePlanEntity failed', {
            error,
            projectId,
            destPhaseId,
            payload
        });
        planFunnel.moveError({
            entityId: payload.entityId,
            toPhaseId: destPhaseId
        });
        // Rollback via background reconciliation — the caller only needs the
        // rejection for its toast.
        refreshBothInBackground();
        throw error;
    }
};
